import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { StatusNames } from '@/modules/employee/employee.types';
import { MatchedEmployee } from '@/modules/employer/components/Lockbox/lockbox.types';

const getFullStatusName = (firstLetter: keyof typeof StatusNames) => StatusNames[firstLetter];


interface SearchEmployees {
  isTooMany?: boolean;
  matchedEmployees?: MatchedEmployee[];
}

interface Props {
  searchValue: string;
  disabled?: boolean;
}

const useGetMatchedEmployeesQuery = ({ searchValue, disabled }: Props) => {
  const { data, isLoading } = useQuery(
    [API_URL.EMPLOYEES_MATCHES, searchValue],
    () =>
      api.get(API_URL.EMPLOYEES_MATCHES, {
        params: { search_value: searchValue },
      }),
    {
      enabled: !!searchValue && !disabled,
    }
  );

  const matchedEmployees: SearchEmployees = useMemo<SearchEmployees>(
    () => ({
      isTooMany: data?.data?.is_too_many,
      matchedEmployees: data?.data?.matched_employees?.map((matchedEmployee) => ({
        employeeId: toString(matchedEmployee?.employee_id),
        employeeName: matchedEmployee?.employee_name,
        ssn: matchedEmployee?.ssn,
        birthDate: matchedEmployee?.birth_date,
        employmentStatus: getFullStatusName(matchedEmployee?.employment_status as keyof typeof StatusNames),
        organizationId: toString(matchedEmployee?.organization_id),
        organizationName: matchedEmployee?.organization_name,
        accountId: toString(matchedEmployee?.account_id),
        accountNumber: matchedEmployee?.account_number,
        availableAmount: matchedEmployee?.available_amount,
        investmentsBalance: matchedEmployee?.investments_balance,
        accountStatus: matchedEmployee?.account_status,
        investmentStatus: matchedEmployee?.investment_status,
        // TODO: this field is missing
        // @ts-ignore
        labelTypes: matchedEmployee?.label_types,
        mailingAddress: matchedEmployee?.mailing_address
          ? {
              line1: matchedEmployee?.mailing_address?.line1,
              line2: matchedEmployee?.mailing_address?.line2 ?? undefined,
              city: matchedEmployee?.mailing_address?.city,
              state: matchedEmployee?.mailing_address?.state,
              zipcode: matchedEmployee?.mailing_address?.zipcode,
            }
          : undefined,
      })),
    }),
    [data]
  );

  return {
    foundEmployees: matchedEmployees?.matchedEmployees,
    isTooMany: matchedEmployees?.isTooMany,
    isLoading,
  };
};

export default useGetMatchedEmployeesQuery;
